import { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next'
import getConfig from 'next/config'

import { CMSBannerData } from 'banner/Banner'
import { Request, Response } from 'express'
import { getCanonicalUrl } from 'head/utils/seoHelpers'
import { serverRequestService } from 'server/services/serverRequestService'
import { logger } from 'server/utils/logger'
import { createMosaicClient } from 'server/utils/mosaicClient'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { sizes } from 'shared/lib/media'
import { parse } from 'shared/lib/safeJSON'
import {
  Breadcrumb,
  CollectionsDirectory,
  Currency,
  FeatureFlags,
  ShoppingRegionData,
} from 'shared/types'
import { MosaicFooterResponse } from 'shared/types/MosaicFooter'
import { sortBreadcrumbChildren } from 'shared/utils/breadcrumb-utils'
import { toFooterBreadcrumb } from 'shared/utils/mosaicFooterUtils'

export type FCGetServerSidePropsContext = GetServerSidePropsContext & {
  req: Request &
    NextApiRequest & {
      /** Banner object set in the server request object by the bannerInjector middleware */
      cmsBanner: string
      /** Active raffles JSON set by the raffleInjector middleware */
      activeRaffles?: string
      /** Collections directory JSON set by the collectionsDirectoryInjector middleware */
      collectionsDirectory?: string
    }
  res: Response & NextApiResponse
}

export type GetServerPageProps = {
  /** Array of currencies */
  availableCurrencies: Currency[]
  /** Canonical URL for current page */
  canonicalUrl: string
  /** Banner object set in the server request object by the bannerInjector middleware */
  cmsBanner: CMSBannerData
  /** Active raffles JSON set by the raffleInjector middleware */
  activeRaffles: string
  /** Collections directory rendered above the footer */
  collectionsDirectory: CollectionsDirectory
  /** Normalized version of the request URL that strips the _next/data prefix
   * for client transitions and includes original query values. */
  currentPath: string
  /** Has auth cookie  */
  isAuthenticated: boolean
  /** Feature Flags object set in req object by the feature flags middleware */
  featureFlags: FeatureFlags
  /** Footer object  */
  footerData: Breadcrumb
  /** Active locale */
  locale: string
  /** Shopping region object for the ShoppingRegionContextProvider */
  shoppingRegionData: ShoppingRegionData
  /** SSR width used to determine the initial breakpoint for the responsive design */
  initWidth: number
}

/**
 * This function is intended to be used in all Pages that fetch data from the server.
 * It returns the props used in the custom App component.
 * @param context  - augemented Next.js GetServerSidePropsContext
 */
export const getServerPageProps = async (
  context: FCGetServerSidePropsContext,
): Promise<GetServerPageProps> => {
  const { req, res, resolvedUrl, locale, defaultLocale, locales } = context
  const activeLocale = locale || defaultLocale || 'en'
  const {
    activeRaffles: reqActiveRaffles,
    cmsBanner: reqCmsBanner,
    collectionsDirectory: reqCollectionsDirectory,
    cookies,
    featureFlags = {},
    originalUrl,
    availableCurrencies,
    shoppingRegionData,
  } = req
  const cmsBanner = parse(reqCmsBanner) as CMSBannerData
  const collectionsDirectory = (parse(reqCollectionsDirectory || '[]') ||
    []) as CollectionsDirectory
  const canonicalUrl = getCanonicalUrl(
    req,
    resolvedUrl,
    originalUrl,
    activeLocale,
    defaultLocale || '',
    locales || [],
  )

  const isAuthenticated = !!cookies?.jwt

  let footerData: Breadcrumb = { children: [], name: '', slug: '' }

  const isNewFooterEnabled = featureFlags[FeatureFlag.TEMP_WEB_FC_ENABLE_NEW_FOOTER]

  const useMosaicFooterData =
    isNewFooterEnabled || featureFlags[FeatureFlag.TEMP_WEB_FC_ENABLE_OLD_FOOTER_MOSAIC_DATA]

  try {
    if (useMosaicFooterData) {
      const { serverRuntimeConfig } = getConfig()
      const { mosaicNavigationNamespace } = serverRuntimeConfig
      const mosaicClient = createMosaicClient(req, activeLocale)
      const footerSlug = isNewFooterEnabled ? 'footer' : 'footer-old'
      const { data } = await mosaicClient.get<MosaicFooterResponse>(
        `/api/v1/mosaic/${mosaicNavigationNamespace}/${footerSlug}`,
      )
      footerData = toFooterBreadcrumb(data)
    } else {
      const { data } = await serverRequestService(req, res, 'sneakers').get(
        '/api/v1/breadcrumbs/footer?includeChildren=true',
      )
      footerData = sortBreadcrumbChildren(data)
    }
  } catch (error) {
    logger.error(error, 'Error fetching footer data.')
  }

  // Set width when SSR
  // Mobile automation sends a is_mobile cookie so that we can force mobile sizing when SSR
  const isMobileAutomation = cookies.is_mobile ?? false
  const initWidth = !isMobileAutomation ? 0 : sizes.extraSmall

  return {
    activeRaffles: reqActiveRaffles || '[]',
    availableCurrencies,
    canonicalUrl,
    cmsBanner,
    collectionsDirectory,
    currentPath: resolvedUrl,
    isAuthenticated,
    featureFlags,
    footerData,
    locale: activeLocale,
    shoppingRegionData,
    initWidth,
  }
}
